import React, { useEffect, useState } from "react";
import { Form, FormGroup, FormSelect, Row } from "react-bootstrap";
import PropTypes from 'prop-types';
import messages from "../views/Dashboard/Dashboard.messages";
import { injectIntl } from "react-intl";

// Services
import { jobGetJob } from "../services/job";
import useFormatter from "../hooks/useFormatter";

// Components
import AlertDialog from "./AlertDialog";
import useBankAccount from "../hooks/useBankAccount";
import { useDebouncedCallback } from "use-debounce";
import useEnv from "../hooks/useEnv";

const ReturnPaymentStep3 = (props) => {
    const { formatCurrency } = useFormatter();
    const { getEnv } = useEnv();
    const [accountTypes, setAccountTypes] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const { getSupportedBankTypes, validateBankAccount } = useBankAccount();
    const [ bicDisabled, setBicDisabled ] = useState(false);
    const [ accountNumberIsValid, setAccountNumberIsValid ] = useState(false);

    const lng = props.intl.formatMessage;
    const form = props.form;
    const job = jobGetJob();
    
    const currency = (job?.currency && job.currency !== '') ? job.currency : getEnv('DEFAULT_CURRENCY');

    const debouncedAccountCheck = useDebouncedCallback((accountType, accountNumber) => {
        validateBankAccount(accountType, accountNumber).then(result => {
            setAccountNumberIsValid(result?.is_valid ?? false);
            if (result?.is_valid && result?.bic !== null && result?.bic !== undefined && result?.bic !== '') {
                setBicDisabled(true);
                handleInputUpdate({ target: { name: 'bankAccountBic', value: result?.bic } });
            } else if (accountType !== 'iban') {
                setBicDisabled(true);
                handleInputUpdate({ target: { name: 'bankAccountBic', value: '' } });
            } else {
                setBicDisabled(false);
            }
        })}, 500);

    useEffect(() => {
        // Do not manipulate the form if account types have not been loaded yet
        if (accountTypes.length === 0) {
            return;
        }
        if (form.bankAccountType === '' || form.bankAccountType === null || form.bankAccountType === undefined) {
            handleInputUpdate({ target: { name: 'bankAccountNumber', value: '' } });
            handleInputUpdate({ target: { name: 'bankAccountBic', value: '' } });
            handleInputUpdate({ target: { name: 'bankAccountHolderName', value: '' } });
            setBicDisabled(true);
            setAccountNumberIsValid(false);
        } else {
            debouncedAccountCheck(form.bankAccountType, form.bankAccountNumber);
        }
    }, [form.bankAccountType, form.bankAccountNumber, accountTypes]);

    useEffect(() => {
        const fetchBankTypes = async () => {
            try {
                const types = await getSupportedBankTypes();
                setAccountTypes(types);
            } catch (error) {
                console.error("Error fetching bank types:", error);
                setAccountTypes([]);
            }
        }
        void fetchBankTypes();
    }, []);

    /**
     * Handle errors
     */
    useEffect(() => {
        // Set error to alertDialog if one is received
        if (props.error?.message !== '') {
            setErrorMessage(props.error.message);
        }
        
    }, [props.error]);
    
    /**
     * Handle changes in input fields and remove errors.
     *
     * @param e
     */
    const handleInputUpdate = (e) => {
        // Clear the error message
        setErrorMessage('');
        props.onFormChange(e);
    }

    const shouldValidateAccountNumber = ['iban', 'bban'].includes(form.bankAccountType);

    return (
        <>
            {job !== null && (
                <Row>
                    <p className='text-dark' id='bank-account-details-desc'>{lng(messages.bankAccountDetailsDescription)}</p>
                    <p className='fw-bold' aria-live="polite">
                        {lng(messages.returnAmount) }: { formatCurrency(job.modoverpaidamount, currency)}<br/>
                        <span className='text-danger'>{lng(messages.refundProcessingFee)}: {formatCurrency(job.modoverpaidfee, currency)}</span>
                    </p>
                </Row>
            )}
            <Row>
                <h4 className='mt-2 mb-3' id='bank-account-details-title'>{lng(messages.bankAccountDetailsTitle)}</h4>
            </Row>
            {errorMessage !== '' && (
                <AlertDialog
                    message={errorMessage}
                    type='danger'
                />
            )}
            <Form id='returnpayment-accountdata-form' aria-labelledby='bank-account-details-title' aria-describedby='bank-account-details-desc' noValidate>
                {/* Account type */}
                <FormGroup controlId="accounttype" className="form-floating mb-3">
                    <FormSelect
                        id='returnpayment-account-type' className="form-control"
                        onChange={handleInputUpdate}
                        name='bankAccountType'
                        required
                        value={form.bankAccountType}
                        aria-label={lng(messages.accountType)}
                    >
                        {['', ...accountTypes].map(option => (
                            <option key={option} value={option}>
                                {option !== '' ? lng(messages[option]): '-'}
                            </option>
                        ))}
                    </FormSelect>
                    <label htmlFor='returnpayment-account-type'>{lng(messages.accountType)}</label>
                </FormGroup>

                {/* Bank account number */}
                <Form.Floating className='mb-3'>
                    <Form.Control
                        id='returnpayment-account-number'
                        type='text'
                        placeholder={form.bankAccountType === 'bban' ? lng(messages.accountNumberInclClearing) : lng(messages.accountNumber)}
                        autoComplete='off'
                        onChange={handleInputUpdate}
                        required
                        isValid={accountNumberIsValid}
                        isInvalid={shouldValidateAccountNumber && form.bankAccountNumber !== '' && !accountNumberIsValid}
                        name='bankAccountNumber'
                        value={form.bankAccountNumber}
                        aria-required="true"
                        aria-label={form.bankAccountType === 'bban' ? lng(messages.accountNumberInclClearing) : lng(messages.accountNumber)}
                    />
                    <Form.Label htmlFor='returnpayment-account-number'>
                        {form.bankAccountType === 'bban' ? lng(messages.accountNumberInclClearing) : lng(messages.accountNumber)}
                    </Form.Label>
                </Form.Floating>

                {/* BIC */}
                <Form.Floating className='mb-3'>
                    <Form.Control
                        id='returnpayment-account-bic'
                        disabled={bicDisabled}
                        type='text'
                        placeholder={lng(messages.bic)}
                        autoComplete='off'
                        onChange={handleInputUpdate}
                        aria-required="true"
                        aria-label={lng(messages.bic)}
                        name='bankAccountBic'
                        value={form.bankAccountBic}
                    />
                    <Form.Label htmlFor='returnpayment-account-bic'>{lng(messages.bic)}</Form.Label>
                </Form.Floating>

                {/* Bank account holder name */}
                <Form.Floating className='mb-3'>
                    <Form.Control
                        id='returnpayment-account-holder-name'
                        type='text'
                        placeholder={lng(messages.bankAccountHolderName)}
                        autoComplete='off'
                        onChange={handleInputUpdate}
                        required
                        aria-required="true"
                        aria-label={lng(messages.bankAccountHolderName)}
                        aria-describedby="bank-account-holder-info"
                        name='bankAccountHolderName'
                        value={ form.bankAccountHolderName }
                    />
                    <Form.Label htmlFor='returnpayment-account-holder-name'>{lng(messages.bankAccountHolderName)}</Form.Label>
                    <Form.Text
                        muted
                        className="d-flex ps-1"
                        id="bank-account-holder-info"
                    >
                        {lng(messages.bankAccountHolderInfo)}
                    </Form.Text>
                </Form.Floating>
                {props.showSaveSettings && (
                    <div className='mb-3 d-flex gap-2 rounded border-secondary-subtle border-1 border pt-2 pb-1 ps-3 pe-2'>
                        <Form.Check
                            id='save-settings-checkbox'
                            type='checkbox'
                            onChange={(e) => props.setSaveSettings(e.target.checked)}
                            checked={props.saveSettings}
                            aria-label={lng(messages.saveDetailsForFutureRefunds)}
                            name='saveBankAccountDetails'
                        />
                        <div>
                            <Form.Label htmlFor='save-settings-checkbox'>
                                {lng(messages.saveDetailsForFutureRefunds)}
                            </Form.Label>
                        </div>
                    </div>
                )}
            </Form>
        </>
    );
};

ReturnPaymentStep3.propTypes = {
    intl: PropTypes.object.isRequired,
    error: PropTypes.object,
    form: PropTypes.object.isRequired,
    onFormChange: PropTypes.func.isRequired,
    saveSettings: PropTypes.bool.isRequired,
    setSaveSettings: PropTypes.func.isRequired,
    showSaveSettings: PropTypes.bool.isRequired
};

export default injectIntl(ReturnPaymentStep3);
